import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Trash2, Calculator, Copy, Check, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DEFAULT_PROFIT_PERCENTS = [15, 20, 25, 30, 35, 40];
const fmt = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);

const QuoteCalculatorPage = () => {
  const [costHeads, setCostHeads] = useState([]);
  const [items, setItems] = useState([{ label: '', amount: '', overhead: '' }]);
  const [lumpsumCost, setLumpsumCost] = useState('');
  const [lumpsumOverhead, setLumpsumOverhead] = useState('');
  const [numDays, setNumDays] = useState('');
  const [numHours, setNumHours] = useState('');
  const [numCandidates, setNumCandidates] = useState('');
  const [customProfit, setCustomProfit] = useState('');
  const [customRevenue, setCustomRevenue] = useState('');
  const [customPerDay, setCustomPerDay] = useState('');
  const [customPerHour, setCustomPerHour] = useState('');
  const [customPerCandidate, setCustomPerCandidate] = useState('');
  const [copied, setCopied] = useState(false);

  // Column visibility toggles
  const [showPerDay, setShowPerDay] = useState(true);
  const [showPerHour, setShowPerHour] = useState(true);
  const [showPerCandidate, setShowPerCandidate] = useState(true);
  const [showPerDayCandidate, setShowPerDayCandidate] = useState(true);

  useEffect(() => {
    fetch(`${API}/quotes/cost-heads`).then(r => r.json()).then(setCostHeads).catch(() => {});
  }, []);

  const addItem = () => setItems([...items, { label: '', amount: '', overhead: '' }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => {
    const updated = [...items];
    updated[i] = { ...updated[i], [field]: val };
    setItems(updated);
  };

  const totalItemCost = useMemo(() => {
    return items.reduce((sum, item) => {
      const amt = parseFloat(item.amount) || 0;
      const oh = parseFloat(item.overhead) || 0;
      return sum + amt + (amt * oh / 100);
    }, 0);
  }, [items]);

  const totalLumpsum = useMemo(() => {
    const base = parseFloat(lumpsumCost) || 0;
    const oh = parseFloat(lumpsumOverhead) || 0;
    return base + (base * oh / 100);
  }, [lumpsumCost, lumpsumOverhead]);

  const totalCost = totalItemCost + totalLumpsum;
  const days = parseFloat(numDays) || 0;
  const hours = parseFloat(numHours) || 0;
  const candidates = parseFloat(numCandidates) || 0;

  // Build rows: default profit percents + custom entries from revenue/perDay/perCandidate/profit%
  const tableRows = useMemo(() => {
    const rows = [];
    const addedPcts = new Set();

    // Add default profit percent rows
    DEFAULT_PROFIT_PERCENTS.forEach(pct => {
      if (totalCost > 0) {
        const rev = totalCost / (1 - pct / 100);
        rows.push({ pct, revenue: rev, source: 'default' });
        addedPcts.add(pct);
      }
    });

    // Custom profit %
    const cp = parseFloat(customProfit);
    if (cp > 0 && !addedPcts.has(cp) && totalCost > 0) {
      const rev = totalCost / (1 - cp / 100);
      rows.push({ pct: cp, revenue: rev, source: 'custom_pct' });
      addedPcts.add(cp);
    }

    // Custom revenue → derive profit %
    const cr = parseFloat(customRevenue);
    if (cr > 0 && totalCost > 0) {
      const pct = ((cr - totalCost) / cr) * 100;
      if (!addedPcts.has(Math.round(pct * 100) / 100)) {
        rows.push({ pct: Math.round(pct * 100) / 100, revenue: cr, source: 'custom_revenue' });
      }
    }

    // Custom per day revenue → derive total revenue → profit %
    const cpd = parseFloat(customPerDay);
    if (cpd > 0 && days > 0 && totalCost > 0) {
      const rev = cpd * days;
      const pct = ((rev - totalCost) / rev) * 100;
      if (pct > 0) {
        const rounded = Math.round(pct * 100) / 100;
        if (!addedPcts.has(rounded)) { rows.push({ pct: rounded, revenue: rev, source: 'custom_perday' }); }
      }
    }

    // Custom per hour revenue → derive total revenue → profit %
    const cph = parseFloat(customPerHour);
    if (cph > 0 && hours > 0 && totalCost > 0) {
      const rev = cph * hours;
      const pct = ((rev - totalCost) / rev) * 100;
      if (pct > 0) {
        const rounded = Math.round(pct * 100) / 100;
        if (!addedPcts.has(rounded)) { rows.push({ pct: rounded, revenue: rev, source: 'custom_perhour' }); }
      }
    }

    // Custom per candidate revenue → derive total revenue → profit %
    const cpc = parseFloat(customPerCandidate);
    if (cpc > 0 && candidates > 0 && totalCost > 0) {
      const rev = cpc * candidates;
      const pct = ((rev - totalCost) / rev) * 100;
      if (pct > 0 && !addedPcts.has(Math.round(pct * 100) / 100)) {
        rows.push({ pct: Math.round(pct * 100) / 100, revenue: rev, source: 'custom_percandidate' });
      }
    }

    // Sort by profit %
    rows.sort((a, b) => a.pct - b.pct);
    return rows;
  }, [totalCost, customProfit, customRevenue, customPerDay, customPerHour, customPerCandidate, days, hours, candidates]);

  // Cost breakdown for hover tooltip
  const costBreakdown = useMemo(() => {
    const parts = [];
    items.forEach(item => {
      if (item.label && item.amount) {
        const amt = parseFloat(item.amount) || 0;
        const oh = parseFloat(item.overhead) || 0;
        const total = amt + (amt * oh / 100);
        parts.push({ label: item.label, amount: total, hasOH: oh > 0, ohPct: oh });
      }
    });
    if (totalLumpsum > 0) {
      parts.push({ label: 'Lumpsum', amount: totalLumpsum, hasOH: (parseFloat(lumpsumOverhead) || 0) > 0, ohPct: parseFloat(lumpsumOverhead) || 0 });
    }
    return parts;
  }, [items, totalLumpsum, lumpsumOverhead]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quote-calculator-page">
      <div className="bg-indigo-950 text-white py-6 px-8">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Training Quote Calculator</h1>
            <p className="text-indigo-300 text-sm mt-1">Calculate pricing for corporate training programs</p>
          </div>
          <Button variant="outline" size="sm" className="text-white border-white/30 hover:bg-white/10" onClick={handleCopyLink} data-testid="copy-link-btn">
            {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cost Breakdown */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Cost Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {items.map((item, i) => (
                <div key={i} className="flex gap-2 items-end">
                  <div className="flex-1">
                    {i === 0 && <Label className="text-xs text-slate-500">Cost Head</Label>}
                    <Select value={item.label} onValueChange={(v) => updateItem(i, 'label', v)}>
                      <SelectTrigger data-testid={`cost-head-select-${i}`}>
                        <SelectValue placeholder="Select or type" />
                      </SelectTrigger>
                      <SelectContent>
                        {costHeads.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                        <SelectItem value="__custom">Custom...</SelectItem>
                      </SelectContent>
                    </Select>
                    {item.label === '__custom' && (
                      <Input className="mt-1" placeholder="Custom label" onChange={(e) => updateItem(i, 'label', e.target.value)} data-testid={`custom-head-${i}`} />
                    )}
                  </div>
                  <div className="w-28">
                    {i === 0 && <Label className="text-xs text-slate-500">Amount</Label>}
                    <Input type="number" placeholder="0" value={item.amount} onChange={(e) => updateItem(i, 'amount', e.target.value)} data-testid={`cost-amount-${i}`} />
                  </div>
                  <div className="w-20">
                    {i === 0 && <Label className="text-xs text-slate-500">OH %</Label>}
                    <Input type="number" placeholder="0" value={item.overhead} onChange={(e) => updateItem(i, 'overhead', e.target.value)} data-testid={`cost-overhead-${i}`} />
                  </div>
                  <Button variant="ghost" size="icon" className="text-red-500 shrink-0" onClick={() => removeItem(i)} disabled={items.length === 1} data-testid={`remove-cost-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addItem} data-testid="add-cost-item-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Cost Head
              </Button>
              <div className="text-right text-sm text-slate-600 pt-2 border-t">
                Subtotal (with overhead): <span className="font-semibold text-indigo-950">{fmt(totalItemCost)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Right Column */}
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Lumpsum Cost (Optional)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs text-slate-500">Total Lumpsum</Label>
                    <Input type="number" placeholder="e.g. 75000" value={lumpsumCost} onChange={(e) => setLumpsumCost(e.target.value)} data-testid="lumpsum-cost-input" />
                  </div>
                  <div className="w-24 space-y-1">
                    <Label className="text-xs text-slate-500">OH %</Label>
                    <Input type="number" placeholder="0" value={lumpsumOverhead} onChange={(e) => setLumpsumOverhead(e.target.value)} data-testid="lumpsum-overhead-input" />
                  </div>
                </div>
                {totalLumpsum > 0 && (
                  <div className="text-right text-sm text-slate-600 pt-2 mt-2 border-t">
                    Lumpsum (with overhead): <span className="font-semibold text-indigo-950">{fmt(totalLumpsum)}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Training Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Number of Days</Label>
                    <Input type="number" step="0.5" placeholder="e.g. 5 or 2.5" value={numDays} onChange={(e) => setNumDays(e.target.value)} data-testid="num-days-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Number of Hours</Label>
                    <Input type="number" step="0.5" placeholder="e.g. 8 or 1.5" value={numHours} onChange={(e) => setNumHours(e.target.value)} data-testid="num-hours-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Number of Candidates</Label>
                    <Input type="number" step="1" placeholder="e.g. 20" value={numCandidates} onChange={(e) => setNumCandidates(e.target.value)} data-testid="num-candidates-input" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Custom Inputs Section */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Custom Quote Inputs</CardTitle>
                <p className="text-xs text-slate-500">Enter any value to add a row to the pricing table</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Custom Profit %</Label>
                    <Input type="number" placeholder="e.g. 33" value={customProfit} onChange={(e) => setCustomProfit(e.target.value)} data-testid="custom-profit-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Custom Total Revenue</Label>
                    <Input type="number" placeholder="e.g. 500000" value={customRevenue} onChange={(e) => setCustomRevenue(e.target.value)} data-testid="custom-revenue-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Custom Per Day Revenue</Label>
                    <Input type="number" placeholder="e.g. 100000" value={customPerDay} onChange={(e) => setCustomPerDay(e.target.value)} data-testid="custom-perday-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Custom Per Hour Revenue</Label>
                    <Input type="number" placeholder="e.g. 12500" value={customPerHour} onChange={(e) => setCustomPerHour(e.target.value)} data-testid="custom-perhour-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Custom Per Candidate Revenue</Label>
                    <Input type="number" placeholder="e.g. 25000" value={customPerCandidate} onChange={(e) => setCustomPerCandidate(e.target.value)} data-testid="custom-percandidate-input" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Total Cost Summary */}
        <Card className="bg-indigo-950 text-white">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calculator className="w-6 h-6 text-emerald-400" />
                <span className="text-lg font-semibold">Total Cost</span>
              </div>
              <span className="text-3xl font-bold" data-testid="total-cost-display">{fmt(totalCost)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Pricing Table */}
        {totalCost > 0 && tableRows.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Pricing Table</CardTitle>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-slate-500 font-medium">Show columns:</span>
                  {days > 0 && (
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <Checkbox checked={showPerDay} onCheckedChange={setShowPerDay} className="h-3.5 w-3.5" />
                      <span className="text-slate-600">Per Day</span>
                    </label>
                  )}
                  {hours > 0 && (
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <Checkbox checked={showPerHour} onCheckedChange={setShowPerHour} className="h-3.5 w-3.5" />
                      <span className="text-slate-600">Per Hour</span>
                    </label>
                  )}
                  {candidates > 0 && (
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <Checkbox checked={showPerCandidate} onCheckedChange={setShowPerCandidate} className="h-3.5 w-3.5" />
                      <span className="text-slate-600">Per Candidate</span>
                    </label>
                  )}
                  {days > 0 && candidates > 0 && (
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <Checkbox checked={showPerDayCandidate} onCheckedChange={setShowPerDayCandidate} className="h-3.5 w-3.5" />
                      <span className="text-slate-600">Per Day/Candidate</span>
                    </label>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="profit-table">
                  <thead>
                    <tr className="border-b-2 border-indigo-100">
                      <th className="text-right py-2 px-3 text-slate-500 font-medium">Revenue (excl. GST)</th>
                      {days > 0 && showPerDay && <th className="text-right py-2 px-3 text-slate-500 font-medium">Per Day</th>}
                      {hours > 0 && showPerHour && <th className="text-right py-2 px-3 text-slate-500 font-medium">Per Hour</th>}
                      {candidates > 0 && showPerCandidate && <th className="text-right py-2 px-3 text-slate-500 font-medium">Per Candidate</th>}
                      {days > 0 && candidates > 0 && showPerDayCandidate && <th className="text-right py-2 px-3 text-slate-500 font-medium">Per Day/Candidate</th>}
                      <th className="text-right py-2 px-3 text-slate-500 font-medium">Total Cost</th>
                      <th className="text-right py-2 px-3 text-slate-500 font-medium">Profit</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">Profit %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row) => {
                      const profit = row.revenue - totalCost;
                      const isCustom = row.source !== 'default';
                      return (
                        <tr key={`${row.pct}-${row.source}`} className={`border-b border-slate-100 hover:bg-slate-50 ${isCustom ? 'bg-emerald-50' : ''}`} data-testid={`profit-row-${row.pct}`}>
                          <td className="py-2.5 px-3 text-right font-bold text-indigo-950">{fmt(row.revenue)}</td>
                          {days > 0 && showPerDay && <td className="py-2.5 px-3 text-right">{fmt(row.revenue / days)}</td>}
                          {hours > 0 && showPerHour && <td className="py-2.5 px-3 text-right">{fmt(row.revenue / hours)}</td>}
                          {candidates > 0 && showPerCandidate && <td className="py-2.5 px-3 text-right">{fmt(row.revenue / candidates)}</td>}
                          {days > 0 && candidates > 0 && showPerDayCandidate && <td className="py-2.5 px-3 text-right">{fmt(row.revenue / (days * candidates))}</td>}
                          <td className="py-2.5 px-3 text-right text-slate-600 relative group cursor-help">
                            {fmt(totalCost)}
                            {/* Hover tooltip with cost breakdown */}
                            {costBreakdown.length > 0 && (
                              <div className="absolute z-50 bottom-full right-0 mb-2 w-64 bg-indigo-950 text-white rounded-lg shadow-xl p-3 text-xs hidden group-hover:block">
                                <p className="font-semibold mb-2 border-b border-white/20 pb-1">Cost Breakdown</p>
                                {costBreakdown.map((c, i) => (
                                  <div key={i} className="flex justify-between py-0.5">
                                    <span>{c.label}{c.hasOH ? ` (+${c.ohPct}% OH)` : ''}</span>
                                    <span>{fmt(c.amount)}</span>
                                  </div>
                                ))}
                                <div className="flex justify-between pt-1 mt-1 border-t border-white/20 font-bold">
                                  <span>Total</span>
                                  <span>{fmt(totalCost)}</span>
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-right text-emerald-600 font-medium">{fmt(profit)}</td>
                          <td className="py-2.5 px-3 font-semibold">{row.pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default QuoteCalculatorPage;
